import {useRemakePatientTherapySequence} from '@/modules/patients-therapies/api/use-remake-patient-therapy-sequence'
import {SequenceActionButton} from '@/modules/patients-therapies/components/sequence-action-button'
import {isDestructiveAction, isMissingPrescribedName} from '@/modules/patients-therapies/functions'
import {type Action, useCanStartRemake, useTherapySequenceActions} from '@/modules/patients-therapies/hooks'
import {useFormatTherapySequenceLabel} from '@/modules/patients-therapies/hooks/use-format-therapy-sequence-label'
import {useIsValidationBlockedByGfr} from '@/modules/patients-therapies/hooks/use-is-validation-blocked-by-gfr'
import {usePreparationUseBy} from '@/modules/patients-therapies/hooks/use-preparation-use-by'
import {getDefaultParameterFormValues} from '@/modules/patients-therapies/schemas'
import {openPatientTherapySequenceDrawerById} from '@/modules/patients-therapies/components/patient-therapy-sequence-drawer/util'
import {openPatientRecordWindow} from '@/modules/patients/components/patient-record-window/util'
import {usePatientTherapySequenceContext} from '@/shared/contexts/patient-therapy-sequence-context'
import {Alert, Button, HStack} from '@chakra-ui/react'
import {PatientTherapySequenceStatus} from '@cypro/engine/database/schema'
import {PatientTherapySequenceEventType} from '@cypro/engine/mutators'
import {isBlockedByPastTherapyDate, isUseByGuardedEvent} from '@cypro/engine/utils'
import {DrawerFooter} from '@cypro/ui/components'
import {withForm} from '@cypro/ui/integrations/form'
import {useStore} from '@tanstack/react-form'
import {useTranslate} from '@tolgee/react'
import {LuExternalLink} from 'react-icons/lu'

// Workflow precedence: the first event type available to the current actor is the
// highlighted button, the rest render as outline buttons next to it.
const ACTION_PRIORITY: string[] = [
  PatientTherapySequenceEventType.enum.ADMINISTER,
  PatientTherapySequenceEventType.enum.RELEASE_FOR_DELIVERY,
  PatientTherapySequenceEventType.enum.COMPLETE_PRODUCTION,
  PatientTherapySequenceEventType.enum.CANCEL_PRODUCTION,
  PatientTherapySequenceEventType.enum.START_PRODUCTION,
  PatientTherapySequenceEventType.enum.PHARMACIST_VALIDATE,
  PatientTherapySequenceEventType.enum.DOCTOR_VALIDATE,
  PatientTherapySequenceEventType.enum.APPROVE_BUDGET,
  PatientTherapySequenceEventType.enum.PATIENT_ARRIVED,
  PatientTherapySequenceEventType.enum.REVERSE_PATIENT_ARRIVED,
  PatientTherapySequenceEventType.enum.REVISE,
  PatientTherapySequenceEventType.enum.RETURN_TO_PHARMACY,
  PatientTherapySequenceEventType.enum.SPOILAGE,
  PatientTherapySequenceEventType.enum.REJECT_BUDGET,
]

export const SequenceFooter = withForm({
  defaultValues: getDefaultParameterFormValues(),
  props: {} as {onSubmit: (action: Action) => void | Promise<unknown>},
  render: function Render({form, onSubmit}) {
    const {t} = useTranslate('common')
    const {sequence} = usePatientTherapySequenceContext()
    const {actions} = useTherapySequenceActions(sequence)
    const remake = useRemakePatientTherapySequence()

    const canRemake = useCanStartRemake(sequence)
    const canValidate = actions.includes(PatientTherapySequenceEventType.enum.PHARMACIST_VALIDATE)
    const isSequenceInThePast = isBlockedByPastTherapyDate(sequence)
    const validationBlockedByGfr = useIsValidationBlockedByGfr(sequence)
    const formMedicine = useStore(form.store, state => state.values.medicine) ?? []
    const missingTradeName = isMissingPrescribedName(formMedicine)
    const missingTradeNameIds = new Set(
      formMedicine.filter(medicine => !medicine.prescribedNameId).map(medicine => medicine.id)
    )
    const missingTradeNameNames =
      sequence.medicine
        ?.filter(medicine => missingTradeNameIds.has(medicine.id))
        .map(medicine => medicine.activeSubstance?.name)
        .filter((name): name is string => Boolean(name)) ?? []

    const validationWarning = !canValidate
      ? null
      : isSequenceInThePast
        ? t('therapy_sequence_disabled_because_is_passed')
        : missingTradeName
          ? t('prescription_tradename_required')
          : validationBlockedByGfr
            ? t('auc_gfr_required')
            : null

    let drawerActions = actions
    if (sequence.status === PatientTherapySequenceStatus.enum.in_production) {
      // completing production happens in the cleanroom, so the drawer only offers Cancel production
      drawerActions = actions.filter(action => action !== PatientTherapySequenceEventType.enum.COMPLETE_PRODUCTION)
    }

    const sorted = drawerActions.toSorted((a, b) => ACTION_PRIORITY.indexOf(a) - ACTION_PRIORITY.indexOf(b))
    // Edit/Review is never the highlighted action — always outline, even when alone
    const primary = sorted.find(action => action !== PatientTherapySequenceEventType.enum.REVISE)
    const secondaries = sorted.filter(action => action !== primary)

    return (
      <DrawerFooter display="flex" flexDirection="column" alignItems="stretch" width="100%" gapY="2">
        {sequence.remadeAt && sequence.remadeToId && (
          <Alert.Root status="info" size="sm" borderRadius="l2" width="100%">
            <Alert.Indicator />
            <Alert.Content>
              <Alert.Title fontSize="sm">{t('prescription_was_remade', 'This prescription was remade')}</Alert.Title>
              <Alert.Description fontSize="xs">
                <Button
                  variant="plain"
                  size="xs"
                  onClick={() => openPatientTherapySequenceDrawerById(sequence.remadeToId as string)}
                >
                  {t('open_remade_prescription', 'Open the new prescription')}
                </Button>
              </Alert.Description>
            </Alert.Content>
          </Alert.Root>
        )}

        {validationWarning && (
          <Alert.Root status="warning" size="sm" borderRadius="l2" width="100%">
            <Alert.Indicator />
            <Alert.Content>
              <Alert.Title fontSize="sm">{validationWarning}</Alert.Title>
              {missingTradeName && missingTradeNameNames.length > 0 && (
                <Alert.Description fontSize="xs">
                  {t('missing')}: {missingTradeNameNames.join(', ')}
                </Alert.Description>
              )}
            </Alert.Content>
          </Alert.Root>
        )}

        <HStack
          display="flex"
          flexDirection={{base: 'column', md: 'row'}}
          justifyContent="space-between"
          alignItems={{base: 'center', md: 'center'}}
          width="100%"
          gapY="2"
        >
          {sequence.patientId && (
            <Button
              variant="outline"
              colorPalette="gray"
              width={{base: '100%', md: 'auto'}}
              onClick={() => openPatientRecordWindow(sequence.patientId)}
            >
              <LuExternalLink />
              {t('open_patient_record')}
            </Button>
          )}

          <HStack gapX="2" width={{base: '100%', md: 'auto'}} justifyContent="flex-end">
            {secondaries.map(secondary => (
              <FooterAction
                key={secondary}
                form={form}
                primary={false}
                action={secondary}
                onClick={() => onSubmit(secondary)}
              />
            ))}
            {primary && <FooterAction primary form={form} action={primary} onClick={() => onSubmit(primary)} />}
            {canRemake && (
              <Button colorPalette="cyan" onClick={() => remake({id: sequence.id})}>
                {t('start_remake')}
              </Button>
            )}
          </HStack>
        </HStack>
      </DrawerFooter>
    )
  },
})

interface FooterActionProps {
  action: Action
  primary: boolean
  onClick: () => void | Promise<unknown>
}

const FooterAction = withForm({
  defaultValues: getDefaultParameterFormValues(),
  props: {} as FooterActionProps,
  render: function Render({form, action, primary, onClick}) {
    const {t} = useTranslate('common')
    const {sequence} = usePatientTherapySequenceContext()
    const formatLabel = useFormatTherapySequenceLabel(sequence)
    const isSequenceInThePast = isBlockedByPastTherapyDate(sequence)

    const label = formatLabel(action, 'action')

    // the pharmacist cannot validate until every medicine has a prescription trade name selected;
    // read from the form (live) rather than the sequence (only synced after validation persists)
    const isValidationBlockedByTradeName =
      useStore(
        form.store,
        state => (action === 'PHARMACIST_VALIDATE' && isMissingPrescribedName(state.values.medicine)) || false
      ) ?? false

    // AUC-based dosing needs the patient's GFR; without it the dose math returns 0,
    // so quick validation is disabled and the pharmacist must review the prescription
    const isValidationBlockedByGfr = action === 'PHARMACIST_VALIDATE' && useIsValidationBlockedByGfr(sequence)

    const disabled = isSequenceInThePast || isValidationBlockedByTradeName || isValidationBlockedByGfr

    // an expired bag is never released or administered (CY-327)
    const isExpiryGuarded = isUseByGuardedEvent(action)
    const {isExpired} = usePreparationUseBy(sequence.id, isExpiryGuarded)
    const isBlockedByExpiry = isExpiryGuarded && isExpired

    function getDisabledReason() {
      if (isSequenceInThePast) return t('therapy_sequence_disabled_because_is_passed')
      if (isValidationBlockedByTradeName) return t('prescription_tradename_required')
      if (isValidationBlockedByGfr) return t('auc_gfr_required')
      return null
    }

    function getColorPalette() {
      if (isDestructiveAction(action)) return 'red'
      if (primary) return 'cyan'
      return undefined
    }

    return (
      <SequenceActionButton
        action={action}
        sequence={sequence}
        disabled={disabled || isBlockedByExpiry}
        disabledReason={
          isBlockedByExpiry
            ? t('preparation_expired_blocked', 'Preparation expired: it cannot be released or administered')
            : getDisabledReason()
        }
        colorPalette={getColorPalette()}
        variant={primary ? 'solid' : 'outline'}
        onClick={onClick}
      >
        {label}
      </SequenceActionButton>
    )
  },
})
